import React, { useEffect, useState } from 'react';
import { ArrowUp, ArrowDown, Eye, EyeOff, Loader2, Save, RotateCcw, Home as HomeIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const SECTION_LABELS = {
  latest:         { emoji: '🆕', label: 'Ultime Uscite' },
  trending_movie: { emoji: '🎬', label: 'Film di tendenza' },
  trending_tv:    { emoji: '📺', label: 'Serie TV di tendenza' },
  top_rated:      { emoji: '🏆', label: 'Più Votati' },
};
const LIMITS = [10, 15, 20, 30];
const DEFAULT_SECTIONS = [
  { key: 'latest', enabled: true, limit: 20 },
  { key: 'trending_movie', enabled: true, limit: 20 },
  { key: 'trending_tv', enabled: true, limit: 20 },
  { key: 'top_rated', enabled: true, limit: 20 },
];

/**
 * Admin sub-page: reorder + toggle + resize Home TMDB sections. Saves to /api/home-layout.
 */
export default function AdminHomeLayout() {
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/home-layout');
        if (Array.isArray(data.sections)) setSections(data.sections);
      } catch (_) { /* keep default */ }
      finally { setLoading(false); }
    })();
  }, []);

  const move = (idx, dir) => {
    const next = [...sections];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setSections(next);
    setDirty(true);
  };

  const toggleEnabled = (idx) => {
    const next = [...sections];
    next[idx] = { ...next[idx], enabled: !next[idx].enabled };
    setSections(next);
    setDirty(true);
  };

  const setLimit = (idx, val) => {
    const next = [...sections];
    next[idx] = { ...next[idx], limit: parseInt(val, 10) };
    setSections(next);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/home-layout', { sections });
      setSections(data.sections);
      setDirty(false);
      toast.success('Home aggiornata. Modifiche visibili subito a tutti gli utenti.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Salvataggio fallito');
    } finally { setSaving(false); }
  };

  const resetDefault = () => {
    setSections(DEFAULT_SECTIONS);
    setDirty(true);
  };

  if (loading) return <div className="text-center py-10 text-white/50 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Caricamento…</div>;

  return (
    <div className="space-y-4" data-testid="admin-home-layout">
      <div className="card-surface p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--gold-soft)] flex items-center justify-center flex-shrink-0">
            <HomeIcon className="w-5 h-5 text-[var(--gold)]" />
          </div>
          <div className="flex-1">
            <div className="font-heading text-base font-medium">Gestione della Home</div>
            <div className="text-xs text-white/60 mt-0.5 leading-relaxed">
              Riordina le sezioni, cambia il numero di elementi mostrati e mostra/nascondi ognuna. Le modifiche sono <b>immediate</b> per tutti gli utenti.
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {sections.map((s, idx) => {
          const meta = SECTION_LABELS[s.key] || { emoji: '❓', label: s.key };
          return (
            <div
              key={s.key}
              data-testid={`home-section-${s.key}`}
              className={`card-surface p-3 flex items-center gap-3 transition-opacity ${s.enabled ? '' : 'opacity-50'}`}
            >
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  data-testid={`section-${s.key}-up`}
                  className="w-7 h-6 rounded-md border border-white/10 flex items-center justify-center disabled:opacity-30 hover:border-[var(--gold)]/50 hover:text-[var(--gold)] transition"
                  aria-label="Sposta su"
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => move(idx, 1)}
                  disabled={idx === sections.length - 1}
                  data-testid={`section-${s.key}-down`}
                  className="w-7 h-6 rounded-md border border-white/10 flex items-center justify-center disabled:opacity-30 hover:border-[var(--gold)]/50 hover:text-[var(--gold)] transition"
                  aria-label="Sposta giù"
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium flex items-center gap-1.5">
                  <span>{meta.emoji}</span>{meta.label}
                </div>
                <div className="text-[10px] text-white/40 mt-0.5">Posizione: {idx + 1}</div>
              </div>

              <select
                value={s.limit}
                onChange={(e) => setLimit(idx, e.target.value)}
                data-testid={`section-${s.key}-limit`}
                className="bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-[var(--gold)]/50"
              >
                {LIMITS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>

              <button
                onClick={() => toggleEnabled(idx)}
                data-testid={`section-${s.key}-toggle`}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${s.enabled ? 'bg-[var(--gold)] text-black' : 'bg-white/5 text-white/50 border border-white/10'}`}
                aria-label={s.enabled ? 'Nascondi sezione' : 'Mostra sezione'}
                title={s.enabled ? 'Sezione visibile' : 'Sezione nascosta'}
              >
                {s.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2 pt-2">
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="home-layout-save"
          className="flex-1 h-11 rounded-full bg-[var(--gold)] text-black font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salva modifiche
        </button>
        <button
          onClick={resetDefault}
          data-testid="home-layout-reset"
          className="h-11 px-4 rounded-full border border-white/10 text-white/80 text-xs flex items-center gap-1.5 hover:border-[var(--gold)]/50 transition"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Default
        </button>
      </div>
    </div>
  );
}
